import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { formatPrice, formatArea, formatStatus } from '../../utils/formatters';
import { ArrowLeft, Building2, Home, DollarSign } from 'lucide-react';

const ProjectBlockDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlock();
  }, [id]);

  const fetchBlock = async () => {
    try {
      const response = await api.get(`/project-blocks/${id}`);
      setBlock(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Blok yüklenemedi:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!block) {
    return <div className="text-center py-8">Blok bulunamadı</div>;
  }

  const portfolioColumns = [
    { header: 'Başlık', accessor: 'title' },
    {
      header: "Emlak Tipi",
      accessor: "property_type",
      render: (row) => {
        const code = row.property_type?.code;
        const getPropertyTypeColor = (typeCode) => {
          switch (typeCode) {
            case 'residential': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'land': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'commercial': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPropertyTypeColor(code)}`}>
            {row.property_type?.name || "-"}
          </span>
        );
      },
    },
    { header: 'Oda', render: (row) => row.room_definition?.name || "-" },
    {
      header: 'Kat',
      accessor: 'floor'
    },
    {
      header: 'Metrekare',
      render: (row) => formatArea(row.net_sqm)
    },
    {
      header: 'Liste Fiyatı',
      render: (row) => formatPrice(row.list_price)
    },
    {
      header: "Durum",
      accessor: "status",
      render: (row) => {
        const code = row.status?.code;
        const getStatusColor = (statusCode) => {
          switch (statusCode) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'deposit': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'option': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'manager_option': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            case 'sold': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'rented': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(code)}`}>
            {row.status?.name || "-"}
          </span>
        );
      },
    },
  ];

  const soldCount = block.portfolios?.filter(p => p.status?.code === 'sold' || p.status?.code === 'rented').length || 0;
  const totalCount = block.portfolios?.length || 0;
  const availableCount = totalCount - soldCount;
  const totalValue = block.portfolios?.reduce((sum, p) => sum + (parseFloat(p.list_price) || 0), 0) || 0;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/projects/${block.project?.id}`)}
        >
          <ArrowLeft size={16} />
          Projeye Dön
        </Button>
      </div>

      {/* Header Card */}
      <Card className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{block.name}</h1>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
              <Building2 size={18} />
              <span>{block.project?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${block.sold_out
                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                }`}>
                {block.sold_out ? 'Tükendi' : 'Aktif'}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Değer</div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {formatPrice(totalValue)}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Gayrimenkul</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalCount}</p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
              <Home size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Satışta/Kiralık</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{availableCount}</p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
              <DollarSign size={24} className="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Satıldı/Kiralandı</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{soldCount}</p>
            </div>
            <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
              <DollarSign size={24} className="text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Gayrimenkuller */}
      <Card title={`${block.name} - Gayrimenkuller`} noPadding>
        {block.portfolios && block.portfolios.length > 0 ? (
          <Table
            columns={portfolioColumns}
            data={block.portfolios}
            onView={(row) => navigate(`/portfolios/${row.id}`)}
            onEdit={(row) => navigate(`/portfolios/${row.id}/edit`)}
            onDelete={(row) => {
              if (window.confirm('Bu gayrimenkulü silmek istediğinizden emin misiniz?')) {
                console.log('Delete portfolio:', row.id);
              }
            }}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Bu bloğa ait gayrimenkul bulunmuyor
          </div>
        )}
      </Card>
    </div>
  );
};

export default ProjectBlockDetail;
